import { Dispatch } from 'react';

import {
  Button,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  CircleAdd,
  Download,
  Edit,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  File,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { OverviewPageEvents } from '~/analytics/overviewPageEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { AssessmentAction } from '~/lib/constants/patients.constants';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyNameToDisplayNameMap } from '~/lib/constants/survey/SurveyNameToDisplayNameMap';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useComponentPortalContext } from '~/lib/contexts/ComponentPortalContext';
import {
  onContinueSurvey,
  onDownloadSurveyPdf,
  onRecordNewSurvey,
} from '~/lib/util/surveyActions.util';
import { useGetGroupedAssessmentsQuery } from '~/store/services/assessments';

interface SafetyPlanOverviewProps {
  patientId: string;
  patientFullName: string;
  updateAssessmentState: Dispatch<AssessmentAction>;
}
type SafetyPlanSurveyCardProps = {
  patientFullName: string;
  surveyData: SurveyResponse;
  updateAssessmentState: Dispatch<AssessmentAction>;
};

const { caption, labelBold, paragraphBold } = EquipUITheme.typography;
const { primary, white } = EquipUIFireflyV2Theme.colors;
const TOP_LEVEL_ID = 'safety-plan-overview-section';

const ctaIconCommonProps = {
  fill: primary[100],
  height: '12px',
  width: '12px',
};

const emptySurveyData = {
  jsonResults: '',
  responseExternalId: '',
  status: '',
  surveyDefinition: '',
  surveyExternalId: '',
  surveyName: '',
};

const SurveyActionCta = ({
  patientFullName,
  surveyData,
  updateAssessmentState,
}: SafetyPlanSurveyCardProps) => {
  const { trackEvent } = useAnalytics();
  const { setComponentVisibility } = useComponentPortalContext();
  const { status, surveyName, responseExternalId } = surveyData;

  let ctaTitle = 'Add Plan';
  let ctaIcon = <CircleAdd {...ctaIconCommonProps} />;

  let onClickFunction = () => {
    const eventName =
      surveyName === SurveyName.SAFETY_PLAN
        ? OverviewPageEvents.safetyPlanAddPlanOverviewTabButtonClicked
        : OverviewPageEvents.lethalMeansAddPlanOverviewTabButtonClicked;

    trackEvent(eventName, {
      Source: AnalyticsSourceValues.OVERVIEW_TAB,
    });

    onRecordNewSurvey(surveyName as SurveyName, updateAssessmentState);
  };

  if (status === SurveyResponseStatus.DONE) {
    ctaTitle = 'Download';
    ctaIcon = <Download {...ctaIconCommonProps} fill={white} />;
    onClickFunction = () => {
      const eventName =
        surveyName === SurveyName.SAFETY_PLAN
          ? OverviewPageEvents.safetyPlanDownloadOverviewTabButtonClicked
          : OverviewPageEvents.lethalMeansDownloadOverviewTabButtonClicked;

      trackEvent(eventName, {
        Source: AnalyticsSourceValues.OVERVIEW_TAB,
      });
      onDownloadSurveyPdf(responseExternalId, [surveyData], patientFullName);
    };
  } else if (
    status === SurveyResponseStatus.IN_PROGRESS ||
    status === SurveyResponseStatus.NEW
  ) {
    ctaTitle = 'Continue';
    ctaIcon = <Edit {...ctaIconCommonProps} />;
    onClickFunction = () => {
      const eventName =
        surveyName === SurveyName.SAFETY_PLAN
          ? OverviewPageEvents.safetyPlanContinueOverviewTabButtonClicked
          : OverviewPageEvents.lethalMeansContinueOverviewTabButtonClicked;

      trackEvent(eventName, {
        Source: AnalyticsSourceValues.OVERVIEW_TAB,
      });
      onContinueSurvey(
        responseExternalId,
        [surveyData],
        setComponentVisibility,
      );
    };
  }

  return (
    <ChakraFlex>
      {status === SurveyResponseStatus.DONE && (
        <Button
          height="26px"
          id={`${TOP_LEVEL_ID}__view-cta-${responseExternalId}`}
          onClick={() => {
            const eventName =
              surveyName === SurveyName.SAFETY_PLAN
                ? OverviewPageEvents.safetyPlanViewOverviewTabButtonClicked
                : OverviewPageEvents.lethalMeansViewOverviewTabButtonClicked;

            trackEvent(eventName, {
              Source: AnalyticsSourceValues.OVERVIEW_TAB,
            });
            updateAssessmentState({
              data: surveyData,
              field: 'selectedSurveyResults',
            });
          }}
          rightIcon={<File {...ctaIconCommonProps} />}
          size="sm"
          variant="transparent"
          width="fit-content"
        >
          <ChakraText {...labelBold} color="primary.100">
            View
          </ChakraText>
        </Button>
      )}
      <Button
        height="26px"
        id={`${TOP_LEVEL_ID}__cta-${responseExternalId}`}
        onClick={onClickFunction}
        rightIcon={ctaIcon}
        size="sm"
        variant={status === SurveyResponseStatus.DONE ? 'solid' : 'transparent'}
        width="fit-content"
      >
        <ChakraText
          {...labelBold}
          color={status === SurveyResponseStatus.DONE ? 'white' : 'primary.100'}
        >
          {ctaTitle}
        </ChakraText>
      </Button>
    </ChakraFlex>
  );
};

const SafetyPlanSurveyCard = ({
  patientFullName,
  surveyData,
  updateAssessmentState,
}: SafetyPlanSurveyCardProps) => {
  const { responseExternalId, status, surveyName, updatedDate } = surveyData;
  const surveyDisplayTitle = SurveyNameToDisplayNameMap[surveyName];

  return (
    <>
      <ChakraBox
        background="white"
        borderColor="neutral.line"
        borderRadius="16px"
        borderStyle="solid"
        borderWidth="1px"
        id={`${TOP_LEVEL_ID}__card-${responseExternalId}`}
        margin="16px 0px"
        padding="16px"
      >
        <ChakraFlex alignItems="center" justifyContent="space-between">
          <ChakraFlex
            direction="column"
            id={`${TOP_LEVEL_ID}__title-container-${responseExternalId}`}
          >
            <ChakraText
              id={`${TOP_LEVEL_ID}__title-${responseExternalId}`}
              {...paragraphBold}
            >
              {surveyDisplayTitle}
            </ChakraText>
            {status === SurveyResponseStatus.DONE && (
              <ChakraText
                color="neutral.secondary"
                {...caption}
                id={`${TOP_LEVEL_ID}__completed-date-${responseExternalId}`}
              >{`Finalized on ${DateTime.fromISO(updatedDate).toFormat(
                'LL/dd/yyyy',
              )}
            `}</ChakraText>
            )}
          </ChakraFlex>
          <SurveyActionCta
            patientFullName={patientFullName}
            surveyData={surveyData}
            updateAssessmentState={updateAssessmentState}
          />
        </ChakraFlex>
      </ChakraBox>
    </>
  );
};

const SafetyPlanOverview = ({
  patientId,
  patientFullName,
  updateAssessmentState,
}: SafetyPlanOverviewProps) => {
  const { data: groupedAssessments, isLoading: isGroupedAssessmentsLoading } =
    useGetGroupedAssessmentsQuery({
      patientId,
    });
  if (isGroupedAssessmentsLoading || !groupedAssessments?.surveys) {
    return <></>;
  }

  const surveyPlans = groupedAssessments.surveys.find(
    ({ name }) => name === SurveyName.SAFETY_PLAN,
  );
  const recentSafetyPlanSurvey: SurveyResponse = surveyPlans
    ?.assessments[0] || {
    ...emptySurveyData,
    surveyName: SurveyName.SAFETY_PLAN,
  };

  const lethalMeansSurveys = groupedAssessments.surveys.find(
    ({ name }) => name === SurveyName.LETHAL_MEANS,
  );
  const recentLethalMeansSurvey: SurveyResponse = lethalMeansSurveys
    ?.assessments[0] || {
    ...emptySurveyData,
    surveyName: SurveyName.LETHAL_MEANS,
  };

  return (
    <>
      {[recentSafetyPlanSurvey, recentLethalMeansSurvey].map((surveyData) => (
        <SafetyPlanSurveyCard
          key={`${TOP_LEVEL_ID}__card-${surveyData?.surveyName}-${surveyData?.responseExternalId}`}
          patientFullName={patientFullName}
          surveyData={surveyData}
          updateAssessmentState={updateAssessmentState}
        />
      ))}
    </>
  );
};

export default SafetyPlanOverview;
